import './file-upload.scss';

import { Progress, notification } from 'antd';
import { type Options } from 'browser-image-compression';
import { useEffect, useRef, useState } from 'react';

import { apiConnector } from '@/api/api.connector';
import FileUploadIcon from '@/media/icons/file-upload.svg?react';
import TrashIcon from '@/media/icons/trash.svg?react';
import {
  calculateFileSizeInMB,
  convertFileToBase64
} from '@/shared/services/helpers/files';
import type { FileType } from '@/shared/types/files.types';

const ALLOWED_IMAGE_EXTENSIONS = ['png', 'jpg', 'jpeg', 'webp'];

interface IProps {
  title?: string;
  file?: FileType;
  helperText?: string;
  error?: string;
  onChange: (file: Partial<FileType>) => void;
  onDelete?: () => void;
  isFrozen?: boolean;
  isRequired?: boolean;
  accept?: string;
  allowedExtensions?: string[];
  compressOptions?: Options;
}

const FileUpload: React.FC<IProps> = ({
  title,
  file,
  helperText,
  error,
  onChange,
  onDelete,
  isFrozen,
  isRequired,
  accept,
  allowedExtensions,
  compressOptions
}) => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [compressIndicator, setCompressIndicator] = useState<number | null>(
    null
  );

  useEffect(() => {
    // Подгрузка файла и установка его в нужном формате в состояние формы
    const fetchBase64 = async () => {
      if (!(file && 'uuid' in file && file?.uuid)) return;

      try {
        const fileUuid = 'uuid' in file ? (file.uuid as string) : '';
        const response = await apiConnector.downloadFile(fileUuid || '');

        const base64Response = await fetch(
          `data:image/webp;base64,${response.base64}`
        );
        const blob = await base64Response.blob();
        const imageFile = new File([blob], response.name);

        onChange({
          base64: response.base64,
          name: imageFile.name,
          size: imageFile.size
        });
      } catch (e) {
        console.error(e);
      }
    };

    if (file && 'uuid' in file && file?.uuid) {
      fetchBase64();
    }
  }, [file]);

  const handleFileChanged = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    let newFile = event.target.files?.[0];
    if (!newFile) return;

    const fileExtension = newFile.name.split('.').pop()?.toLowerCase();
    if (allowedExtensions && !allowedExtensions.includes(fileExtension || '')) {
      const extensionsText = allowedExtensions
        .map((item) => `.${item}`)
        .join(', ');
      notification.error({
        title: 'Недопустимое расширение файла!',
        description: `Пожалуйста, загрузите файл в формате ${extensionsText}`
      });
      return;
    }

    try {
      const fileExtension = newFile.name.split('.').pop()?.toLowerCase() || '';
      if (compressOptions && ALLOWED_IMAGE_EXTENSIONS.includes(fileExtension)) {
        const imageCompression = (await import('browser-image-compression'))
          .default;
        newFile = await imageCompression(newFile, {
          ...compressOptions,
          onProgress: (progress) => {
            setCompressIndicator(progress);
            if (progress === 100) {
              setTimeout(() => {
                setCompressIndicator(null);
              }, 500);
            }
          }
        });
      }
      const file = await convertFileToBase64(newFile);
      const base64 = file[1];
      const format = file[0];
      onChange({
        name: newFile.name,
        base64,
        size: (newFile.size * 4) / 3,
        format,
        file: newFile
      });
    } catch (error) {
      console.error(error);
      onChange({ name: undefined, base64: undefined, size: undefined });
    }
  };

  const isShowDelete = !isFrozen && file;

  return (
    <div className="file-upload">
      <label>
        {(!isFrozen || compressIndicator === null) && (
          <input
            ref={inputRef}
            className="file-upload__field"
            type="file"
            onChange={handleFileChanged}
            accept={accept}
          />
        )}

        <div
          className={`file-upload__view ${
            error ? 'file-upload__view--error' : ''
          }`}
        >
          <FileUploadIcon />

          <div className="file-upload__file-info">
            {!!title && (
              <p className="file-upload__title">
                {title}
                {isRequired && <span className="file-upload__require">*</span>}
              </p>
            )}
            <p
              className={`file-upload__value ${
                !file ? 'file-upload__value--no-file' : ''
              }`}
            >
              {file?.name
                ? file.name
                : `Выбрать файл${allowedExtensions ? ` (${allowedExtensions?.join(', ')})` : ''}`}
              {file?.size && (
                <span className="file-upload__chosen-size">
                  {calculateFileSizeInMB(file.size)} МБ
                </span>
              )}
            </p>
          </div>

          {compressIndicator !== null && (
            <Progress
              className="file-upload__progress"
              percent={compressIndicator}
            />
          )}

          {isShowDelete && (
            <button
              onClick={(e) => {
                e.preventDefault();
                if (inputRef.current) inputRef.current.value = '';
                onDelete?.();
              }}
              className="file-upload__delete"
            >
              <TrashIcon />
            </button>
          )}
        </div>
      </label>

      {error ? (
        <p className="file-upload__additional-text file-upload__additional-text--error">
          {error}
        </p>
      ) : (
        helperText && (
          <p className="file-upload__additional-text">{helperText}</p>
        )
      )}
    </div>
  );
};

export default FileUpload;
